import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import ContentHeader from "../components/ContentHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORE = "msp_admin_key";

const money = (amt, cur) =>
  amt == null ? "—" : `${(amt / 100).toLocaleString("en-US", { style: "currency", currency: (cur || "usd").toUpperCase() })}`;

const Dot = ({ ok }) => (
  <span className={`st-dot ${ok === true ? "st-dot-ok" : ok === false ? "st-dot-bad" : "st-dot-unk"}`} />
);

const AdminStatus = () => {
  const navigate = useNavigate();
  const [adminKey, setAdminKey] = useState(sessionStorage.getItem(KEY_STORE) || "");
  const [authed, setAuthed] = useState(!!sessionStorage.getItem(KEY_STORE));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const headers = useCallback(() => ({ Authorization: `Bearer ${adminKey}` }), [adminKey]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/stripe-status`, { headers: headers() });
      setData(data);
      setAuthed(true);
      sessionStorage.setItem(KEY_STORE, adminKey);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Invalid admin key"); setAuthed(false); }
      else toast.error("Failed to load status");
    } finally { setLoading(false); }
  }, [adminKey, headers]);

  useEffect(() => { if (authed) load(); /* eslint-disable-next-line */ }, []);

  if (!authed) {
    return (
      <div style={{ minHeight: "100vh" }}>
        <ContentHeader />
        <div className="hero-bg" />
        <div className="admin-gate" data-testid="status-gate">
          <h1>Go-Live Status</h1>
          <p style={{ color: "var(--g1)", marginBottom: "1.4rem", fontSize: "0.9rem" }}>Enter your admin key.</p>
          <input className="admin-input" type="password" placeholder="Admin key" value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="status-key-input" />
          <button className="admin-btn" onClick={load} disabled={loading || !adminKey} data-testid="status-login-btn">
            {loading ? "Checking…" : "View Status"}
          </button>
        </div>
      </div>
    );
  }

  const isLive = data?.mode === "live";
  const checklist = data ? [
    { label: "Stripe account linked", ok: !!data.account_id && !data.account_error },
    { label: "Business details submitted (KYC)", ok: data.details_submitted },
    { label: "Can accept charges", ok: data.charges_enabled },
    { label: "Payouts enabled (bank connected)", ok: data.payouts_enabled },
    { label: "$4,995 license product configured", ok: data.price_configured },
    { label: "Payment webhook configured", ok: data.webhook_configured },
    { label: "Live mode active (real payments)", ok: isLive },
  ] : [];

  return (
    <div style={{ minHeight: "100vh" }} data-testid="status-dashboard">
      <ContentHeader />
      <div className="hero-bg" />
      <div className="admin-wrap">
        <div className="admin-header">
          <h1>Payments — Go-Live Status</h1>
          <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
            <button className="admin-btn-ghost" onClick={() => navigate("/admin/articles")} data-testid="status-to-articles">Content Agent</button>
            <button className="admin-btn" onClick={load} disabled={loading} data-testid="status-refresh">{loading ? "Refreshing…" : "Refresh"}</button>
          </div>
        </div>

        {data && (
          <>
            <div className={`st-mode ${isLive ? "st-mode-live" : "st-mode-test"}`} data-testid="status-mode">
              <div className="st-mode-badge">{isLive ? "LIVE MODE" : "TEST MODE"}</div>
              <div className="st-mode-text">
                {isLive
                  ? "Real payments are being accepted into your Stripe account."
                  : "You're in test mode — no real money moves yet. Claim your Stripe account and republish to go live."}
              </div>
            </div>

            <div className="st-cards">
              <div className="st-card" data-testid="status-account">
                <span className="st-card-label">Stripe account</span>
                <div className="st-kv"><span>Account ID</span><code>{data.account_id || "—"}</code></div>
                <div className="st-kv"><span>Business</span><span>{data.business_name || data.account_email || "—"}</span></div>
                <div className="st-kv"><span>Country</span><span>{(data.country || "—").toUpperCase()}</span></div>
                <div className="st-kv"><span>License price</span><span>{data.price_configured ? money(data.price_amount, data.price_currency) : "not set"}</span></div>
                {data.account_error && <div className="st-err" data-testid="status-acct-error">{data.account_error}</div>}
              </div>

              <div className="st-card" data-testid="status-checklist">
                <span className="st-card-label">Go-live checklist</span>
                {checklist.map((c) => (
                  <div className="st-check" key={c.label} data-testid={`status-check-${c.ok ? "ok" : "pending"}`}>
                    <Dot ok={c.ok} /> <span>{c.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {!isLive && (
              <div className="st-steps" data-testid="status-steps">
                <span className="st-card-label">How to go live</span>
                <ol>
                  <li>In Emergent, open <strong>Manage → Payments</strong> and click to claim your Stripe account.</li>
                  <li>Sign in with your existing <strong>envolvp@envolvp.com</strong> Stripe login (don't create a new login).</li>
                  <li>Attach it as a <strong>new, separate account</strong> under that login for "Mortgage Survival Playbook".</li>
                  <li>Complete that account's activation: ENVOLVP LLC details, EIN, and the <strong>bank account</strong> for payouts.</li>
                  <li><strong>Republish/Deploy</strong> the app — live keys swap in automatically. Come back here and hit Refresh.</li>
                </ol>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminStatus;
